import React, { useEffect, useState } from 'react';
import { AlertTriangle, CalendarCheck, Scissors, Trophy } from 'lucide-react';
import { api } from '../lib/api';

/**
 * Rankings del mes por agenda (dirección, 1/10/2026): qué persona DA más
 * cirugías en cada agenda de quirófano y más consultas previas en cada agenda
 * de consulta. Lo cuenta el servidor (GET /api/indicadores/ranking-agendas,
 * routers/indicadores_router.ranking_agendas) y aquí se pinta, una tabla por
 * agenda: puesto, persona y citas, el total y lo que no entra en el ranking.
 *
 * Qué es «consulta previa» no lo decide la pantalla ni el código del tipo:
 * la marca `es_consulta_previa` de cada tipo de cita (Gestión de agendas →
 * Conceptos de cita). Sin ningún tipo marcado se dice, en vez de enseñar ceros.
 */
export const URL_RANKING_AGENDAS = '/api/indicadores/ranking-agendas';

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// Lo que no entra en el ranking de una agenda, dicho en una línea.
function aparteDe(a) {
  const trozos = [];
  if (a.retoques) trozos.push(plural(a.retoques, 'retoque', 'retoques'));
  if (a.chalazion) trozos.push(plural(a.chalazion, 'chalazión', 'chalazión'));
  if (a.no_quirurgicas) trozos.push(plural(a.no_quirurgicas, 'cita que no es una cirugía', 'citas que no son una cirugía'));
  if (a.sin_registrar) trozos.push(`${a.sin_registrar} sin registrar quién la dio`);
  if (a.de_sistema) trozos.push(`${a.de_sistema} de cuentas del sistema`);
  // Las de quien no sale en Indicadores (04/10/2026): Usuarios → «Sale en Indicadores».
  if (a.fuera_del_equipo) trozos.push(`${a.fuera_del_equipo} de quien no sale en Indicadores`);
  return trozos.join(' · ');
}

// Cuántas de las de esa persona están hoy anuladas o «no acude».
function estadoDe(f) {
  const trozos = [];
  if (f.anuladas) trozos.push(plural(f.anuladas, 'anulada', 'anuladas'));
  if (f.no_acude) trozos.push(`${f.no_acude} no acude`);
  return trozos.join(' · ');
}

function TablaDeAgenda({ agenda, unidad, testid }) {
  const filas = Array.isArray(agenda.filas) ? agenda.filas : [];
  const aparte = aparteDe(agenda);
  return (
    <div className="cjo-card overflow-hidden min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-[rgba(46,110,94,0.04)]">
        <span className="font-semibold text-sm text-[#13231E] min-w-0 break-words">
          {agenda.nombre}
          {agenda.activa === false ? (
            <span className="ml-1.5 align-middle rounded border border-border px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Retirada
            </span>
          ) : null}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums" data-testid={`${testid}-total`}>
          {plural(agenda.total || 0, unidad[0], unidad[1])}
        </span>
      </div>
      {filas.length === 0 ? (
        <p className="px-3 py-3 text-sm text-muted-foreground" data-testid={`${testid}-vacia`}>
          {agenda.fuera_del_equipo
            ? 'Ninguna de las personas que salen en Indicadores ha dado citas aquí este mes.'
            : 'Nadie ha dado ninguna este mes.'}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground border-b border-border">
              <th className="py-1.5 pl-3 pr-2 w-12 font-medium">Puesto</th>
              <th className="py-1.5 pr-2 font-medium">Usuario</th>
              <th className="py-1.5 pr-3 text-right font-medium">Citas</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => {
              const primero = f.puesto === 1;
              const estado = estadoDe(f);
              return (
                <tr key={f.usuario_id}
                  className={`border-b border-border last:border-0 ${primero ? 'bg-amber-50/60 dark:bg-amber-950/20' : ''}`}
                  data-testid={`${testid}-fila-${f.usuario_id}`}>
                  <td className="py-1.5 pl-3 pr-2 tabular-nums" data-testid={`${testid}-fila-${f.usuario_id}-puesto`}>
                    <span className="inline-flex items-center gap-1">
                      {f.puesto}
                      {primero ? <Trophy className="w-3.5 h-3.5 text-amber-500" aria-label="Primer puesto" /> : null}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2 min-w-0 break-words">
                    <span className="font-medium">{f.usuario || '—'}</span>
                    {estado ? (
                      <span className="block text-[11px] text-muted-foreground" data-testid={`${testid}-fila-${f.usuario_id}-estado`}>
                        {estado}
                      </span>
                    ) : null}
                  </td>
                  <td className={`py-1.5 pr-3 text-right tabular-nums font-semibold ${primero ? 'text-amber-600' : ''}`}
                    data-testid={`${testid}-fila-${f.usuario_id}-citas`}>
                    {f.citas}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {aparte ? (
        <p className="px-3 py-2 border-t border-border text-[11px] text-muted-foreground" data-testid={`${testid}-aparte`}>
          No entran: {aparte}.
        </p>
      ) : null}
    </div>
  );
}

function Bloque({ titulo, icono: Icono, bloque, unidad, testid, children }) {
  const agendas = Array.isArray(bloque?.agendas) ? bloque.agendas : [];
  const totales = bloque?.totales || {};
  return (
    <section className="mt-6" data-testid={testid}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-2">
        <h3 className="text-sm font-semibold flex items-center gap-2 text-[#13231E]">
          <Icono className="w-4 h-4" /> {titulo}
        </h3>
        <span className="text-xs text-muted-foreground tabular-nums" data-testid={`${testid}-total`}>
          Total del mes: {plural(totales.total || 0, unidad[0], unidad[1])}
          {totales.anuladas || totales.no_acude
            ? ` (${[totales.anuladas ? plural(totales.anuladas, 'anulada', 'anuladas') : '',
              totales.no_acude ? `${totales.no_acude} no acude` : ''].filter(Boolean).join(' · ')})`
            : ''}
        </span>
      </div>
      {children}
      {agendas.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid={`${testid}-sin-agendas`}>
          No hay agendas de este tipo.
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {agendas.map((a) => (
            <TablaDeAgenda key={a.agenda_id} agenda={a} unidad={unidad} testid={`${testid}-${a.agenda_id}`} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function RankingAgendasMes({ mes }) {
  const [datos, setDatos] = useState(null);
  const [fallo, setFallo] = useState('');

  useEffect(() => {
    let off = false;
    setDatos(null);
    setFallo('');
    api.get(URL_RANKING_AGENDAS, { params: { mes } })
      .then((r) => { if (!off) setDatos(r.data); })
      // Un fallo de lectura no es un mes sin citas: se dice.
      .catch((e) => { if (!off) setFallo(e?.response?.data?.detail || 'no se ha podido consultar'); });
    return () => { off = true; };
  }, [mes]);

  if (fallo) {
    return (
      <p className="mt-6 text-sm text-red-700" data-testid="ranking-agendas-fallo">
        No se ha podido consultar el ranking por agenda de este mes ({fallo}). Esto no es un cero:
        vuelve a intentarlo.
      </p>
    );
  }
  if (datos === null) {
    return <p className="mt-6 text-sm text-muted-foreground">Cargando el ranking por agenda…</p>;
  }
  // Un servidor que aún no lo cuenta no pinta nada (en vez de ceros).
  if (!datos || typeof datos !== 'object' || !datos.cirugias || !datos.consultas_previas) return null;

  const cp = datos.consultas_previas;
  const tipos = Array.isArray(cp.tipos) ? cp.tipos : [];
  return (
    <div className="mt-8" data-testid="ranking-agendas">
      <Bloque titulo="Cirugías agendadas por usuario" icono={Scissors} bloque={datos.cirugias}
        unidad={['cirugía', 'cirugías']} testid="ranking-cirugias" />
      {cp.sin_marca ? (
        <section className="mt-6" data-testid="ranking-consultas">
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-2 text-[#13231E]">
            <CalendarCheck className="w-4 h-4" /> Consultas previas agendadas por usuario
          </h3>
          <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
            data-testid="ranking-consultas-sin-marca">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Ningún tipo de cita está marcado como «consulta previa», así que este ranking no
              puede contar nada. Márcalos en Gestión de agendas → Conceptos de cita («Es una
              consulta previa»).
            </span>
          </p>
        </section>
      ) : (
        <Bloque titulo="Consultas previas agendadas por usuario" icono={CalendarCheck} bloque={cp}
          unidad={['consulta previa', 'consultas previas']} testid="ranking-consultas">
          {tipos.length ? (
            <p className="text-xs text-muted-foreground mb-2" data-testid="ranking-consultas-tipos">
              Cuentan los tipos marcados como consulta previa: {tipos.join(', ')}.
            </p>
          ) : null}
        </Bloque>
      )}
      <p className="text-xs text-muted-foreground mt-3" data-testid="ranking-agendas-regla">
        Los dos rankings cuentan las citas que cada persona ha DADO ese mes (hora de Madrid), no las
        que ocurren ese mes. «Cirugías» son las intervenciones de cada agenda de quirófano; los
        retoques, los chalazión y lo que no es una cirugía (una revisión citada allí) van aparte. «Consultas previas» son las citas de cada
        agenda de consulta con un tipo marcado como consulta previa. Las anuladas y las «no acude»
        cuentan, porque la cita se dio, y cada persona lleva debajo cuántas de las suyas lo están.
        Una cita movida de agenda cuenta en la agenda donde está ahora. Las citas sin registrar
        quién las dio (las importadas del programa antiguo) y las de cuentas del sistema no entran.
        Con alguien marcado en Usuarios («Sale en Indicadores») solo compiten las marcadas: las
        citas de las demás tampoco entran y se dicen aparte.
        Una agenda retirada solo sale si ese mes tiene alguna cita.
      </p>
    </div>
  );
}
